import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, dateIT } from "@/lib/format";
import { Building2, CalendarDays, Wallet, MessageSquareText, Plus, TrendingUp, ArrowUpRight } from "lucide-react";

export default function Dashboard() {
  const { org, user } = useAuth();
  const navigate = useNavigate();
  const [props, setProps] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [msgCount, setMsgCount] = useState(0);

  useEffect(() => {
    api.get("/properties").then(({ data }) => setProps(data)).catch(() => {});
    api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/messages").then(({ data }) => setMsgCount(data.length)).catch(() => {});
  }, []);

  const upcoming = bookings.filter((b) => b.checkin && new Date(b.checkin) >= new Date(new Date().toDateString())).slice(0, 5);

  const stats = [
    { label: "Immobili", value: props.length, icon: Building2, action: () => navigate("/properties") },
    { label: "Prenotazioni", value: bookings.length, icon: CalendarDays, action: () => navigate("/calendar") },
    { label: "Borsellino", value: eur(org?.wallet_balance_cents), icon: Wallet, action: () => navigate("/messages") },
    { label: "Messaggi inviati", value: msgCount, icon: MessageSquareText, action: () => navigate("/messages") },
  ];

  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Ciao {user?.name?.split(" ")[0]}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Panoramica</h1>
        </div>
        <button onClick={() => navigate("/properties")} className="bg-primary text-primary-foreground font-medium px-4 py-2.5 rounded-lg shadow-sm hover:opacity-95 active:scale-[0.98] transition-all flex items-center gap-2" data-testid="dashboard-add-property">
          <Plus className="h-4 w-4" /> Aggiungi Immobile
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <button key={s.label} onClick={s.action} className="bg-card rounded-xl border border-border/70 p-5 flex flex-col text-left hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,0,0,0.06)] hover:border-primary/40 transition-all" data-testid={`stat-${s.label}`}>
            <div className="flex items-center justify-between">
              <s.icon className="h-5 w-5 text-primary" />
              <ArrowUpRight className="h-4 w-4 text-muted-foreground/40" />
            </div>
            <div className="font-display text-2xl font-bold mt-4">{s.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-card rounded-xl border border-border/80 p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">Prossimi arrivi</h2>
            <button onClick={() => navigate("/calendar")} className="text-sm text-primary hover:underline">Calendario</button>
          </div>
          {upcoming.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              <CalendarDays className="h-10 w-10 mx-auto mb-3 opacity-30" />
              Nessun arrivo in programma. Aggiungi una prenotazione dal calendario di un immobile.
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {upcoming.map((b) => (
                <div key={b.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{b.guest_name || "Ospite"}</div>
                    <div className="text-xs text-muted-foreground">{b.property_name} · {dateIT(b.checkin)} → {dateIT(b.checkout)}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-700 font-medium capitalize">{b.source === "manual" ? "Manuale" : b.source}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-[hsl(168,62%,16%)] to-[hsl(190,55%,22%)] text-white rounded-xl p-6 shadow-xl relative overflow-hidden">
          <TrendingUp className="h-24 w-24 absolute -right-4 -bottom-4 opacity-10" />
          <div className="text-xs uppercase tracking-wider opacity-80">Credito Borsellino</div>
          <div className="font-mono-data text-3xl font-bold mt-2">{eur(org?.wallet_balance_cents)}</div>
          <p className="text-sm opacity-80 mt-2">Email 0,02 € · WhatsApp da 0,06 €</p>
          <button onClick={() => navigate("/messages")} className="mt-5 w-full bg-white/15 hover:bg-white/25 backdrop-blur rounded-lg py-2.5 text-sm font-medium transition-colors">
            Ricarica borsellino
          </button>
        </div>
      </div>
    </div>
  );
}
